import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Save, Plus, Trash2, Building2 } from "lucide-react";

const EMPTY_DRAFT = { name_en: "", name_de: "" };

/** Sysadmin-only reference list of departments (English/German), assigned to
 * employees via Employee.department_id. See department_mapping.py for the
 * one-time free-text backfill this list feeds. */
export default function DepartmentsAdmin() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [editingId, setEditingId] = useState(null); // department id, or "new"
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const refresh = () =>
    client.get("/departments").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => { refresh(); }, []);

  const startEdit = (dep) => {
    setDraft({ name_en: dep.name_en, name_de: dep.name_de });
    setEditingId(dep.id);
  };

  const startAdd = () => {
    setDraft(EMPTY_DRAFT);
    setEditingId("new");
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const save = async () => {
    if (!draft.name_en.trim() || !draft.name_de.trim()) {
      toast.error(t("departments.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      if (editingId === "new") {
        await client.post("/departments", draft);
      } else {
        await client.patch(`/departments/${editingId}`, draft);
      }
      toast.success(t("common.saved"));
      cancel();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await client.delete(`/departments/${deleting.id}`);
      toast.success(t("departments.removed"));
      setDeleting(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("departments.deleteFailed"));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-10" data-testid="departments-page">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <p className="data-label mb-2">{t("departments.breadcrumb")}</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("departments.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("departments.subtitle")}</p>
        </div>
        {editingId === null && (
          <Button data-testid="add-department-btn" className="gap-1.5" onClick={startAdd}>
            <Plus className="h-4 w-4" />
            {t("departments.addDepartment")}
          </Button>
        )}
      </div>

      {!items && <Skeleton className="h-64" />}

      {items && (
        <Card className="p-6" data-testid="departments-card">
          {editingId === "new" && (
            <div className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-primary/30 bg-primary/5 p-3 md:grid-cols-2">
              <Input
                data-testid="department-form-name-en"
                placeholder={t("departments.nameEn")}
                disabled={saving}
                value={draft.name_en}
                onChange={(e) => setDraft({ ...draft, name_en: e.target.value })}
              />
              <Input
                data-testid="department-form-name-de"
                placeholder={t("departments.nameDe")}
                disabled={saving}
                value={draft.name_de}
                onChange={(e) => setDraft({ ...draft, name_de: e.target.value })}
              />
              <div className="col-span-full flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={cancel}>{t("common.cancel")}</Button>
                <Button data-testid="save-new-department-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
                  <Save className="h-3.5 w-3.5" />
                  {t("common.save")}
                </Button>
              </div>
            </div>
          )}

          {items.length === 0 && editingId !== "new" && (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="departments-empty">
              <Building2 className="mx-auto mb-3 h-8 w-8" strokeWidth={1.5} />
              {t("departments.empty")}
            </p>
          )}

          {items.length > 0 && (
            <div className="mb-2 flex items-center gap-3 px-3" data-testid="departments-column-headers">
              <div className="flex flex-1 items-center gap-4">
                <p className="flex-1 text-xs font-semibold uppercase text-muted-foreground">{t("departments.columnEn")}</p>
                <p className="flex-1 text-xs font-semibold uppercase text-muted-foreground">{t("departments.columnDe")}</p>
              </div>
              <div className="w-[76px] shrink-0" />
            </div>
          )}

          <div className="space-y-2">
            {items.map((dep) => (
              <div key={dep.id} className="rounded-md border border-border p-3" data-testid={`department-row-${dep.id}`}>
                {editingId === dep.id ? (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Input
                      data-testid={`department-form-name-en-${dep.id}`}
                      disabled={saving}
                      value={draft.name_en}
                      onChange={(e) => setDraft({ ...draft, name_en: e.target.value })}
                    />
                    <Input
                      data-testid={`department-form-name-de-${dep.id}`}
                      disabled={saving}
                      value={draft.name_de}
                      onChange={(e) => setDraft({ ...draft, name_de: e.target.value })}
                    />
                    <div className="col-span-full flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={cancel}>{t("common.cancel")}</Button>
                      <Button data-testid={`save-department-${dep.id}`} size="sm" onClick={save} disabled={saving} className="gap-1.5">
                        <Save className="h-3.5 w-3.5" />
                        {t("common.save")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-1 items-center gap-4">
                      <p className="flex-1 font-medium">{dep.name_en}</p>
                      <p className="flex-1 text-muted-foreground">{dep.name_de}</p>
                    </div>
                    {editingId === null && (
                      <div className="flex shrink-0 gap-1">
                        <Button variant="ghost" size="icon" data-testid={`edit-department-${dep.id}`} onClick={() => startEdit(dep)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-department-${dep.id}`} onClick={() => setDeleting(dep)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && !deleteBusy && setDeleting(null)}>
        <AlertDialogContent data-testid="department-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{deleting?.name_en}</AlertDialogTitle>
            <AlertDialogDescription>{t("departments.removeConfirm", deleting?.name_en)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleteBusy}
              data-testid="confirm-department-delete-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
